import { IconButton, Tooltip, styled, alpha } from '@mui/material';
import {
  LookDirection,
  useCameraStore,
} from '../SyncedControls/useCameraStore';
import { FC } from 'react';
import { LockReset } from '@mui/icons-material';
import { ControlSection } from '../ui/ControlSection';

export const CameraShortcuts = () => {
  const { selectedLookDirection, toggleLock, isLocked } = useCameraStore();

  return (
    <ControlSection title='Camera Shortcuts'>
      <ShortcutsGrid>
        <div /> {/* Empty cell */}
        <CameraShortcutButton direction='front' />
        <Tooltip
          title={isLocked ? 'Unlock camera' : 'Lock camera'}
          disableInteractive
        >
          <LockButton
            onClick={() => toggleLock()}
            disabled={selectedLookDirection === null}
            size='small'
            color={isLocked ? 'primary' : 'default'}
          >
            <LockReset fontSize='small' />
          </LockButton>
        </Tooltip>
        <CameraShortcutButton direction='left' />
        <CameraShortcutButton direction='top' />
        <CameraShortcutButton direction='right' />
        <div /> {/* Empty cell */}
        <CameraShortcutButton direction='back' />
        <div /> {/* Empty cell */}
      </ShortcutsGrid>
    </ControlSection>
  );
};

interface CameraShortcutButtonProps {
  direction: LookDirection;
}

const CameraShortcutButton: FC<CameraShortcutButtonProps> = ({ direction }) => {
  const { look, selectedLookDirection, isLocked } = useCameraStore();

  const isSelected = selectedLookDirection === direction;
  const isDisabled = isSelected && isLocked;

  const getDirectionLabel = (dir: LookDirection) => {
    if (!dir) return '';
    const labels = {
      front: 'F',
      back: 'B',
      left: 'L',
      right: 'R',
      top: 'T',
    };
    return labels[dir] || '';
  };

  return (
    <Tooltip
      title={isDisabled ? 'Camera locked' : `View ${direction}`}
      disableInteractive
    >
      <DirectionButton
        onClick={() => look(direction)}
        disabled={isDisabled}
        size='small'
        color={isSelected ? 'primary' : 'default'}
      >
        {getDirectionLabel(direction)}
      </DirectionButton>
    </Tooltip>
  );
};

const ShortcutsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: 'repeat(3, 1fr)',
  gap: '4px',
  aspectRatio: '1',
  maxWidth: '120px',
});

const DirectionButton = styled(IconButton)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  borderRadius: '6px',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  color: theme.palette.text.secondary,
  minWidth: 'auto',
  width: '100%',
  height: '100%',
  aspectRatio: '1',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.main,
  },
  '&.MuiIconButton-colorPrimary': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
  },
  '&.Mui-disabled': {
    backgroundColor: alpha(theme.palette.action.disabled, 0.08),
    borderColor: alpha(theme.palette.action.disabled, 0.12),
    color: theme.palette.action.disabled,
  },
}));

const LockButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '6px',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  color: theme.palette.text.secondary,
  width: '100%',
  height: '100%',
  aspectRatio: '1',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.main,
  },
  '&.MuiIconButton-colorPrimary': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
  },
  '&.Mui-disabled': {
    backgroundColor: alpha(theme.palette.action.disabled, 0.08),
    borderColor: alpha(theme.palette.action.disabled, 0.12),
    color: theme.palette.action.disabled,
  },
}));
